import { readFileSync, writeFileSync } from "node:fs";
import { readFile as readFileAsync } from "node:fs/promises";
import { join } from "node:path";
import { minimatch } from "minimatch";
import { isGenerationCommit } from "./git/CommitDetection.js";
import { GitClient } from "./git/GitClient.js";
import { LockfileManager, LockfileNotFoundError } from "./LockfileManager.js";
import { ReplayApplicator } from "./ReplayApplicator.js";
import { isBinaryFile } from "./shared/binary.js";
import { readFernignorePatterns } from "./shared/fernignore.js";
import { ReplayCommitter } from "./ReplayCommitter.js";
import { ReplayDetector } from "./ReplayDetector.js";
import { stripConflictMarkers } from "./conflict-utils.js";
import { computePerPatchDiffWithFallback, synthesizeNewFileDiff, unifiedDiff } from "./PatchRegionDiff.js";
import { containsPlaceholder, neutralizeAutoversionTheirs } from "./autoversion.js";
import { FileOwnership } from "./classifier/FileOwnership.js";
import {
    type Flow,
    FirstGenerationFlow,
    NoPatchesFlow,
    NormalRegenerationFlow,
    SkipApplicationFlow,
} from "./flows/index.js";
import type { DegradedReason, FileResult, FlowKind, ReplayConfig, ReplayResult, StoredPatch } from "./types.js";

export interface ConflictDetail {
    patchId: string;
    patchMessage: string;
    reason?: string;
    files: FileResult[];
    /** Files that applied cleanly in a patch that also had conflicts. */
    cleanFiles?: string[];
}

export interface UnresolvedPatchInfo {
    patchId: string;
    patchMessage: string;
    files: string[];
    conflictDetails: FileResult[];
}

export interface ReplayReport {
    flow: "first-generation" | "no-patches" | "normal-regeneration" | "skip-application";
    patchesDetected: number;
    patchesApplied: number;
    patchesWithConflicts: number;
    patchesSkipped: number;
    patchesAbsorbed?: number;
    patchesRepointed?: number;
    patchesContentRebased?: number;
    patchesKeptAsUserOwned?: number;
    patchesPartiallyApplied?: number;
    patchesConflictResolved?: number;
    patchesReverted?: number;
    patchesRefreshed?: number;
    conflicts: FileResult[];
    conflictDetails?: ConflictDetail[];
    /** Patches that conflicted and need local resolution via `fern-replay resolve` */
    unresolvedPatches?: UnresolvedPatchInfo[];
    wouldApply?: StoredPatch[];
    warnings?: string[];
    /**
     * True iff `degradedReasons` is non-empty: the run completed but could not
     * guarantee customizations were carried forward because the baseline it
     * needed (the previous generation) was unreachable during the run.
     * Both fields are undefined on healthy runs, matching the `warnings`
     * convention. Degraded is observability only — it never changes detection
     * results, lockfile saves, or commit decisions.
     */
    degraded?: boolean;
    degradedReasons?: DegradedReason[];
}

export interface ReplayOptions {
    /** Log what would happen but don't modify anything */
    dryRun?: boolean;
    /** Write files and stage changes, but don't commit */
    stageOnly?: boolean;
    /** CLI version for commit metadata */
    cliVersion?: string;
    /** Generator versions for commit metadata */
    generatorVersions?: Record<string, string>;
    /** Commit generation + update lockfile, skip detection/application */
    skipApplication?: boolean;
}

/**
 * Options honored by `applyPreparedReplay()`. `cliVersion`, `generatorVersions`,
 * `dryRun`, and `skipApplication` were all captured in phase 1
 * and re-supplying them here is meaningless.
 */
export interface ApplyOptions {
    /** Write files and stage changes, but don't commit */
    stageOnly?: boolean;
}

/**
 * Opaque handle returned by `prepareReplay()`. Consumed by `applyPreparedReplay()`
 * to complete the run.
 *
 * Contract for consumers (e.g., generator-cli pipeline inserting AutoVersionStep):
 *
 * - Between `prepareReplay` and `applyPreparedReplay`, HEAD is at the new
 *   `[fern-generated]` commit (unless `_prepared.kind === "terminal"`, e.g. dry-run).
 * - Consumers MAY land additional commits on HEAD between phases. `applyPatches`
 *   runs against current HEAD at phase-2 call time, so mid-phase commits are
 *   respected. `rebasePatches` uses `currentGenerationSha` (the pure `[fern-generated]`
 *   SHA), NOT HEAD — mid-phase commits do not retarget patches' `base_generation`.
 * - The service instance is single-use across the phase boundary: do not call
 *   another `prepareReplay()` between phases. In-memory lockfile and warning
 *   state would be corrupted.
 * - If the caller's mid-phase work throws or `applyPreparedReplay` is never called,
 *   the repo is left with the new `[fern-generated]` commit but a stale lockfile.
 *   Consumers MUST either (a) still call `applyPreparedReplay` so the lockfile
 *   advances, or (b) reset HEAD to `previousGenerationSha` and surface the failure.
 *
 * To decide whether to run autoversion between phases, use:
 *     prep.previousGenerationSha != null && prep.flow !== "skip-application"
 */
export interface ReplayPreparation {
    flow: FlowKind;
    /** lockfile.current_generation from BEFORE this run; null iff no prior generation exists */
    previousGenerationSha: string | null;
    /** SHA of the new [fern-generated] commit created by prepareReplay (or HEAD for dry-run terminals) */
    currentGenerationSha: string;
    /** @internal Opaque state consumed by applyPreparedReplay */
    readonly _prepared: InternalPreparationState;
}

/**
 * @internal
 * Opaque state passed from `prepareReplay` to `applyPreparedReplay`.
 *
 * Discriminated by `kind`:
 * - `"terminal"` — phase 2 returns `preparedReport` without disk/git work
 *   (dry-run, first-generation with no patch work, no-patches with no
 *   detection results). Apply is a no-op.
 * - `"active"` — phase 2 must apply patches, rebase, save lockfile, commit.
 *   Carries the patch set + cached phase-1 state.
 */
type InternalPreparationState = InternalPreparationTerminal | InternalPreparationActive;

interface InternalPreparationTerminal {
    readonly kind: "terminal";
    readonly flow: FlowKind;
    readonly preparedReport: ReplayReport;
}

interface InternalPreparationActive {
    readonly kind: "active";
    readonly flow: FlowKind;
    /** post-preGenRebase, post-detection, post-absorption (references, not copies) */
    patchesToApply: StoredPatch[];
    /** subset of patchesToApply that were freshly detected this cycle (vs already in the lockfile) */
    newPatchIds: Set<string>;
    preRebaseCounts?: { conflictResolved: number; conflictAbsorbed: number; contentRefreshed: number };
    /** detector warnings snapshotted at end of phase 1 */
    detectorWarnings: string[];
    /**
     * Detector degraded reasons snapshotted at end of phase 1 (next to
     * `detectorWarnings`) so degraded state survives the two-phase
     * prepare/apply split and any mid-phase commits (e.g. autoversion).
     */
    detectorDegraded: DegradedReason[];
    revertedCount: number;
    /** [fern-generated] SHA; passed to rebasePatches as currentGenSha */
    genSha: string;
    /** cached phase-1 options for constructing the final ReplayReport in phase 2 */
    optionsSnapshot?: ReplayOptions;
}

/** ReplayPreparation narrowed to its active variant (terminal cases handled by dispatcher). */
type ActiveReplayPreparation = ReplayPreparation & { readonly _prepared: InternalPreparationActive };

/**
 * Narrow `ReplayPreparation` to its active variant at the entry of each
 * Flow's `apply()`. Terminal cases are short-circuited in `applyPreparedReplay`
 * before any flow's apply method is called, so this should never fire in practice.
 *
 * @internal Used by Flow implementations in `src/flows/`.
 */
export function assertActive(prep: ReplayPreparation): asserts prep is ActiveReplayPreparation {
    if (prep._prepared.kind !== "active") {
        throw new Error(
            `Flow.apply() called with terminal preparation (kind=${prep._prepared.kind}); ` +
                `terminal cases must be handled by applyPreparedReplay's dispatcher`
        );
    }
}

export class ReplayService {
    /** @internal Used by Flow implementations in `src/flows/`. */
    git: GitClient;
    /** @internal Used by Flow implementations in `src/flows/`. */
    detector: ReplayDetector;
    /** @internal Used by Flow implementations in `src/flows/`. */
    applicator: ReplayApplicator;
    /** @internal Used by Flow implementations in `src/flows/`. */
    committer: ReplayCommitter;
    /** @internal Used by Flow implementations in `src/flows/`. */
    lockManager: LockfileManager;
    /** @internal Used by Flow implementations in `src/flows/`. */
    fileOwnership!: FileOwnership;
    /** @internal Used by Flow implementations in `src/flows/`. */
    outputDir: string;
    /** @internal Test-only. Captures the last ReplayResult[] returned from applicator.applyPatches(). */
    _lastApplyResults: ReplayResult[] = [];
    /**
     * Service-level warnings accumulated during a single runReplay() call.
     * Merged with `detector.warnings` into `ReplayReport.warnings` by each flow handler.
     * Populated by `FileOwnership.resolveCanonical` when a patch's base generation tree
     * is unreachable (shallow clone / aggressive `git gc --prune`) and we fall back to a
     * conservative heuristic. Cleared at the top of `runReplay()`.
     *
     * @internal Used by Flow implementations in `src/flows/`.
     */
    warnings: string[] = [];

    /**
     * Service-level structured degraded reasons, mirroring `warnings`.
     * Populated at apply time (e.g. `recordUnreachableBaseWarnings` when a
     * patch's base-generation tree is unreachable). Merged with
     * `detector.degradedReasons` into `ReplayReport.degradedReasons` by each
     * flow handler. Cleared at the top of `prepareReplay()`.
     *
     * @internal Used by Flow implementations in `src/flows/`.
     */
    degradedReasons: DegradedReason[] = [];

    /**
     * @internal Test-only accessor.
     * Returns the ReplayApplicator instance used for the last run. Tests use this
     * to inspect the inter-patch accumulator after runReplay() completes (invariant I2).
     */
    get applicatorRef(): ReplayApplicator {
        return this.applicator;
    }

    /**
     * @internal Test-only accessor.
     * Returns the ReplayResult[] from the most recent applyPatches() call. Tests use
     * this to filter applied-vs-conflicted-vs-absorbed patches for invariant
     * assertions. Returns an empty array if no patches have been applied yet.
     */
    getLastResults(): ReadonlyArray<ReplayResult> {
        return this._lastApplyResults;
    }

    constructor(outputDir: string, _config: ReplayConfig) {
        const git = new GitClient(outputDir);
        this.git = git;
        this.outputDir = outputDir;
        this.lockManager = new LockfileManager(outputDir);
        // ADR 0002: `.fernignore` patterns plumbed to the detector so it
        // filters fernignore-matched files out at capture time. Patterns
        // are read once at construction; if `.fernignore` changes mid-run
        // the next `ReplayService` instance will pick up the new patterns.
        const fernignorePatterns = this.readFernignorePatterns();
        this.detector = new ReplayDetector(git, this.lockManager, outputDir, fernignorePatterns);
        this.applicator = new ReplayApplicator(git, this.lockManager, outputDir);
        this.committer = new ReplayCommitter(git, outputDir);
        this.fileOwnership = new FileOwnership(git);
    }

    /**
     * Phase 1 of the two-phase replay flow. Runs preGenerationRebase (when applicable),
     * detects new patches, and creates the `[fern-generated]` commit. Leaves HEAD at
     * the generation commit (or unchanged for dry-run).
     *
     * Does NOT apply patches — the returned handle must be passed to
     * `applyPreparedReplay()` to complete the run. No disk writes to the lockfile
     * happen here; lockfile persistence is deferred to phase 2.
     *
     * Callers may land additional commits on HEAD between `prepareReplay` and
     * `applyPreparedReplay` (e.g., `[fern-autoversion]`).
     */
    async prepareReplay(options?: ReplayOptions): Promise<ReplayPreparation> {
        // Reset per-run state so warnings from prior invocations don't leak.
        // Both the service-level and detector-level warnings must be cleared —
        // the two-phase API (`prepareReplay` + `applyPreparedReplay`) enlarges
        // the observability window for callers like generator-cli's autoversion
        // step, so we tighten the per-call cleanup here.
        this.warnings = [];
        this.detector.warnings.length = 0;
        this.degradedReasons = [];
        this.detector.degradedReasons.length = 0;
        // ADR 0002 migration: strip legacy `.fernignore`-matched entries from
        // existing patches in the lockfile. One-shot, idempotent, audited.
        // Runs ONCE per `prepareReplay` so every flow benefits.
        this.cleanseLegacyFernignoreEntries();
        // Strip infrastructure file diffs (e.g. .fernignore itself) that were
        // bundled into patch_content by older detection logic. Prevents
        // .fernignore edits from being silently re-applied on each regen.
        this.cleanseInfrastructureFileEntries();
        return this.selectFlow(options).prepare(options);
    }

    /**
     * ADR 0002 migration step. Strips `.fernignore`-matched entries from
     * `patch.files`, `patch.theirs_snapshot` keys, and `diff --git` sections
     * in `patch.patch_content` for every patch in the lockfile. Patches that
     * end up with no surviving files are removed entirely.
     *
     * Idempotent: a second run is a no-op because the first run already
     * matches the contract. Per-patch try/catch ensures one bad patch does
     * not block the rest. Each affected patch emits a warning naming the
     * stripped paths. `patch_content` is left untouched if no parseable
     * `diff --git` headers are found (fail-safe for exotic legacy formats).
     */
    private cleanseLegacyFernignoreEntries(): void {
        const patterns = this.readFernignorePatterns();
        if (patterns.length === 0) return;
        // No lockfile yet (first-generation flow before bootstrap) — nothing
        // to cleanse.
        if (!this.lockManager.exists()) return;
        // Ensure the lockfile is loaded before mutation.
        this.lockManager.read();

        let lockfileMutated = false;
        const patches = this.lockManager.getPatches();
        for (const patch of patches) {
            try {
                const result = computeLegacyFernignoreCleanse(patch, patterns, (content) =>
                    this.detector.computeContentHash(content),
                );
                if (result.unchanged) continue;
                if (result.remove) {
                    this.lockManager.removePatch(patch.id);
                    this.warnings.push(
                        `Cleansed legacy patch ${patch.id}: all referenced files match .fernignore — ` +
                            `patch removed. Customer's on-disk content for those files is preserved by .fernignore. ` +
                            `(ADR 0002: .fernignore-protected files are not tracked by Replay.)`
                    );
                } else {
                    this.lockManager.updatePatch(patch.id, {
                        files: result.files,
                        patch_content: result.patch_content,
                        content_hash: result.content_hash,
                        ...(result.theirs_snapshot !== undefined
                            ? { theirs_snapshot: result.theirs_snapshot }
                            : {}),
                    });
                    this.warnings.push(
                        `Cleansed legacy patch ${patch.id}: stripped .fernignore-matched entries ` +
                            `(${result.strippedPaths.join(", ")}). Customer's on-disk content for those ` +
                            `files is preserved by .fernignore. (ADR 0002.)`
                    );
                }
                lockfileMutated = true;
            } catch {
                // Per-patch isolation — one bad patch does not block the rest.
                // The patch survives unchanged, which is strictly safer than
                // partial mutation.
            }
        }

        if (lockfileMutated) {
            this.lockManager.save();
        }
    }

    /**
     * Migration step: strips infrastructure file diffs (`.fernignore`, `.fern/*`)
     * from existing patches' `patch_content`. These diffs were bundled by older
     * detection logic that did not scope `git diff` when only infrastructure files
     * were stripped. Patches that end up with no surviving diff sections are
     * removed entirely.
     *
     * Idempotent: a second run is a no-op because the first run already removed
     * all infrastructure sections. Per-patch try/catch ensures isolation.
     */
    private cleanseInfrastructureFileEntries(): void {
        if (!this.lockManager.exists()) return;
        this.lockManager.read();

        let lockfileMutated = false;
        const patches = this.lockManager.getPatches();
        for (const patch of patches) {
            try {
                const result = computeInfrastructureFileCleanse(
                    patch,
                    (content) => this.detector.computeContentHash(content),
                );
                if (result.unchanged) continue;
                if (result.remove) {
                    this.lockManager.removePatch(patch.id);
                    this.warnings.push(
                        `Stripped infrastructure file diffs from patch ${patch.id}: patch contained ` +
                            `only infrastructure file changes (${result.strippedPaths.join(", ")}) — removed.`
                    );
                } else {
                    this.lockManager.updatePatch(patch.id, {
                        patch_content: result.patch_content,
                        content_hash: result.content_hash,
                    });
                    this.warnings.push(
                        `Stripped infrastructure file diffs from patch ${patch.id}: ` +
                            `removed bundled diffs for ${result.strippedPaths.join(", ")}.`
                    );
                }
                lockfileMutated = true;
            } catch {
                // Per-patch isolation
            }
        }

        if (lockfileMutated) {
            this.lockManager.save();
        }
    }

    /**
     * Phase 2 of the two-phase replay flow. Applies patches, rebases them against
     * the generation, saves the lockfile, and commits `[fern-replay]` (or stages
     * under `stageOnly`). Operates on HEAD at call time — mid-phase commits are
     * respected.
     *
     * For terminal handles (dry-run, first-gen has no patch work, skip-app does
     * its own post-commit logic), this returns the precomputed report.
     */
    async applyPreparedReplay(
        prep: ReplayPreparation,
        options?: ApplyOptions
    ): Promise<ReplayReport> {
        if (prep._prepared.kind === "terminal") {
            // Dry-run path: report was fully built in phase 1 and no disk/git work
            // is needed in phase 2.
            return prep._prepared.preparedReport;
        }
        return this.flowForKind(prep._prepared.flow).apply(prep, options);
    }

    /**
     * Construct the flow for the current run. `selectFlow` is called from
     * `prepareReplay` and decides based on options + lockfile state.
     */
    private selectFlow(options?: ReplayOptions): Flow {
        if (options?.skipApplication) return new SkipApplicationFlow(this);
        return this.flowForKind(this.determineFlow());
    }

    /**
     * Construct a flow given its kind. Used by `applyPreparedReplay` to
     * route to the same kind that was selected in phase 1.
     */
    private flowForKind(kind: FlowKind): Flow {
        switch (kind) {
            case "first-generation":
                return new FirstGenerationFlow(this);
            case "no-patches":
                return new NoPatchesFlow(this);
            case "normal-regeneration":
                return new NormalRegenerationFlow(this);
            case "skip-application":
                return new SkipApplicationFlow(this);
        }
    }

    /**
     * Backwards-compatible atomic entry point. Composes `prepareReplay` + `applyPreparedReplay`.
     * Behavior is byte-identical to the pre-split implementation for all existing callers.
     */
    async runReplay(options?: ReplayOptions): Promise<ReplayReport> {
        const prep = await this.prepareReplay(options);
        return this.applyPreparedReplay(prep, options);
    }

    private determineFlow(): "first-generation" | "no-patches" | "normal-regeneration" {
        try {
            const lock = this.lockManager.read();
            return lock.patches.length === 0 ? "no-patches" : "normal-regeneration";
        } catch (error) {
            if (error instanceof LockfileNotFoundError) {
                return "first-generation";
            }
            throw error;
        }
    }

    /**
     * Rebase cleanly applied patches so they are relative to the current generation.
     * This prevents recurring conflicts on subsequent regenerations.
     * Returns the number of patches rebased.
     *
     * @internal Used by Flow implementations in `src/flows/`.
     */
    async rebasePatches(
        results: ReplayResult[],
        currentGenSha: string
    ): Promise<{
        absorbed: number;
        repointed: number;
        contentRebased: number;
        keptAsUserOwned: number;
        absorbedPatchIds: Set<string>;
    }> {
        let absorbed = 0;
        let repointed = 0;
        let contentRebased = 0;
        let keptAsUserOwned = 0;
        // Track content hashes seen this rebase cycle. When two patches end
        // up with byte-identical post-rebase patch_content, they encode the
        // SAME customer-side delta — applying both back-to-back would corrupt
        // the file via duplicate-apply. Consolidate to one (the first seen)
        // and remove the rest. Per-commit attribution is recoverable from
        // `git log`. Map value is the surviving patch's id, used in the
        // consolidation warning.
        const seenContentHashes = new Map<string, string>();
        const absorbedPatchIds = new Set<string>();
        // Cache once — used per patch below to classify protected vs non-protected files.
        const fernignorePatterns = this.readFernignorePatterns();
        // `resolveCanonical` may push a one-time unreachable-base warning per
        // base SHA when `patch.base_generation` is GC'd. Sharing one set across
        // every patch in this rebase keeps the warning to one line per affected
        // base, not one per file.
        const warnedGens = new Set<string>();

        // Pre-pass: Update patch.files with resolved (renamed) paths from application.
        // This ensures downstream operations (user-owned check, git diff, git show)
        // use the current file paths rather than stale pre-rename paths.
        for (const result of results) {
            if (result.resolvedFiles && Object.keys(result.resolvedFiles).length > 0) {
                const patch = result.patch;
                const updatedFiles = patch.files.map((f) => result.resolvedFiles![f] ?? f);
                patch.files = updatedFiles;
                try {
                    this.lockManager.updatePatch(patch.id, { files: updatedFiles });
                } catch {
                    // New patches aren't in lockfile yet — in-memory update sufficient
                }
            }
        }

        // Build set of file paths that any earlier patch CONFLICTED on in this
        // run. The absorption branch below uses this to detect a clean follow-up
        // patch whose disk state was reset to OURS by revertConflictingFiles or
        // applyPatches' cross-patch marker stripping (ReplayApplicator.ts:157-189).
        // Such a patch shows an empty diff vs currentGen and would otherwise be
        // silently absorbed — masking a real customization. Preserve as
        // unresolved instead so the customer can recover via `fern replay resolve`.
        const conflictedFilePaths = new Set<string>();
        for (const r of results) {
            if (r.status !== "conflict") continue;
            if (r.fileResults) {
                for (const fr of r.fileResults) {
                    if (fr.status !== "conflict") continue;
                    conflictedFilePaths.add(fr.file);
                    if (r.resolvedFiles) {
                        for (const [orig, resolved] of Object.entries(r.resolvedFiles)) {
                            if (resolved === fr.file) conflictedFilePaths.add(orig);
                        }
                    }
                }
            } else {
                // Defensive: a conflict result without fileResults isn't produced
                // by the current applicator, but if a future change introduces
                // one, fall back to patch.files so we don't silently regress.
                for (const f of r.patch.files) conflictedFilePaths.add(f);
            }
        }

        for (const result of results) {
            if (result.status === "conflict" && result.fileResults) {
                // For conflict patches with mixed results, trim any clean files
                // that were absorbed by the generator. This prevents absorbed files
                // from poisoning the pre-generation rebase conflict marker check.
                await this.trimAbsorbedFiles(result, currentGenSha);
                continue;
            }

            // Only rebase cleanly applied patches.
            // Conflicted patches keep their old base_generation so they retry.
            if (result.status !== "applied") continue;

            const patch = result.patch;

            // Skip if already based on the current generation
            if (patch.base_generation === currentGenSha) continue;

            try {
                // Classify file ownership via the single canonical resolver.
                // Widens "protected = .fernignore only" to every user-owned
                // file: persisted `user_owned` flag, `.fernignore` patterns,
                // patch_content `--- /dev/null` creation signal, and a tree
                // lookup against `patch.base_generation` (with a fall-back to
                // `currentGenSha` for legacy one-gen lockfiles). This keeps
                // user-created files in mixed composites from being absorbed
                // alongside the generator-modified half.
                const originalByResolved: Record<string, string> = {};
                if (result.resolvedFiles) {
                    for (const [orig, resolved] of Object.entries(result.resolvedFiles)) {
                        originalByResolved[resolved] = orig;
                    }
                }
                const isUserOwnedPerFile = await Promise.all(
                    patch.files.map((file) =>
                        this.fileOwnership.resolveCanonical({
                            file,
                            originalFileName: originalByResolved[file],
                            patch,
                            currentGenSha,
                            fernignorePatterns,
                            warnedGens,
                            warnings: this.warnings,
                        })
                    )
                );
                // Match main's "protected" semantics for the compact subset:
                // .fernignore itself OR fernignore-pattern-matched files. These
                // must never be absorbed even on empty diff.
                const hasProtectedFiles = patch.files.some(
                    (file) => file === ".fernignore" ||
                        fernignorePatterns.some((p) => file === p || minimatch(file, p))
                );
                // The "keep" branch fires only when EVERY file in the patch
                // is user-owned. Mixed composite patches (user + gen files,
                // e.g. from tree-diff fallback) still flow through the
                // absorption/rebase branch below so the gen-file halves get
                // content-rebased; this also avoids regressing the long-horizon
                // accumulator invariants on mixed-ownership composites.
                const allUserOwned = isUserOwnedPerFile.every(Boolean);

                if (allUserOwned && patch.files.length > 0) {
                    const baseChanged = patch.base_generation !== currentGenSha;

                    // Rebase content hash against current generation so both the
                    // no-patches and normal-regeneration flows produce identical
                    // hashes for user-owned patches. preGenerationRebase (normal
                    // flow) converts patch_content to `git diff currentGen HEAD`
                    // and recomputes the hash. Without this, the no-patches flow
                    // keeps the original formatPatch hash, which differs because
                    // the diff base and format are both different.
                    const userDiff = await this.git
                        .exec(["diff", currentGenSha, "--", ...patch.files])
                        .catch(() => null);
                    let userDiffSnapshot: Record<string, string> | null = null;
                    if (userDiff != null && userDiff.trim()) {
                        const newHash = this.detector.computeContentHash(userDiff);
                        patch.patch_content = userDiff;
                        patch.content_hash = newHash;
                        // rebasePatches only operates on `status === "applied"`
                        // patches (clean merges). Working tree is the correctly
                        // merged customer state at the new generation —
                        // including any path renames carried by `patch.files`.
                        // Snapshot capture here is what keeps snapshot keys
                        // aligned with current paths across regen cycles
                        // (W5 rename-churn relies on this).
                        const snap = await this.readSnapshotFromDisk(patch.files);
                        if (Object.keys(snap).length > 0) {
                            userDiffSnapshot = snap;
                            patch.theirs_snapshot = snap;
                        }
                    }

                    patch.base_generation = currentGenSha;
                    try {
                        this.lockManager.updatePatch(patch.id, {
                            base_generation: currentGenSha,
                            ...(!patch.user_owned ? { user_owned: true } : {}),
                            ...(userDiff != null && userDiff.trim()
                                ? {
                                    patch_content: patch.patch_content,
                                    content_hash: patch.content_hash,
                                    ...(userDiffSnapshot != null && Object.keys(userDiffSnapshot).length > 0
                                        ? { theirs_snapshot: userDiffSnapshot }
                                        : {})
                                }
                                : {})
                        });
                    } catch {
                        // Not in lockfile yet — in-memory mutation is sufficient.
                    }
                    patch.user_owned = true;
                    keptAsUserOwned++;
                    // Count as repointed when base advanced — tests track this.
                    if (baseChanged) repointed++;
                    continue;
                }

                // Mixed or generator-only patch — compute diff vs currentGen
                // for absorption/rebase decisions.
                //
                // Note: rebasePatches uses the cumulative diff here (matching
                // preGenerationRebase's behavior). The content-hash dedup at
                // line ~1140 below preserves patches with distinct
                // `original_commit`s when their rebased content_hash
                // converges. Per-patch surgical scoping in this hot path
                // interacts badly with bundled user-owned files (file already
                // in currentGen due to commitGeneration), causing false
                // absorption — kept cumulative here for safety. The
                // customer-facing per-patch-region fix lives in
                // `commands/resolve.ts`.
                // Autoversion-aware rebase: when the raw diff carries the
                // placeholder (`--version AUTO` swap folded in), re-derive the
                // diff and snapshot against a version-neutralized THEIRS so the
                // pipeline-owned version line is never captured. Gated on the
                // cheap `containsPlaceholder(rawDiff)` string check so fixed-
                // version SDKs (no placeholder) keep the original single-`git
                // diff` cost — no extra per-file `showFile` calls.
                const rawDiff = await this.git
                    .exec(["diff", currentGenSha, "--", ...patch.files])
                    .catch(() => null);
                const neutralizedRebase =
                    rawDiff != null && containsPlaceholder(rawDiff)
                        ? await this.computeAutoversionNeutralizedRebase(patch.files, currentGenSha)
                        : null;
                const diff = neutralizedRebase ? neutralizedRebase.diff : rawDiff;

                if (diff === null) continue;

                if (!diff.trim()) {
                    if (hasProtectedFiles) {
                        // Protected files — never absorb. Advance base_generation only.
                        patch.base_generation = currentGenSha;
                        try {
                            this.lockManager.updatePatch(patch.id, { base_generation: currentGenSha });
                        } catch {
                            // Not in lockfile yet — in-memory mutation is sufficient.
                        }
                        keptAsUserOwned++;
                        continue;
                    }
                    // Conflict-by-association guard: do not absorb if a predecessor
                    // in this run conflicted on any of this patch's files. Empty
                    // diff is suspicious here — disk was reset to OURS by marker
                    // stripping, masking a real customization. Preserve as
                    // unresolved so `fern replay resolve` can recover it.
                    const overlapsConflicted = patch.files.some((f) => conflictedFilePaths.has(f));
                    if (overlapsConflicted) {
                        patch.status = "unresolved";
                        try {
                            this.lockManager.updatePatch(patch.id, { status: "unresolved" });
                        } catch {
                            // New patch — addPatch() in the caller carries the
                            // in-memory status through into the lockfile.
                        }
                        this.warnings.push(
                            `Patch ${patch.id} (${patch.original_message}) was preserved as unresolved ` +
                                `because an earlier patch conflicted on the same file(s) (${patch.files.join(", ")}). ` +
                                `Run \`fern replay resolve\` to apply this customization manually.`
                        );
                        continue;
                    }
                    // Generator file with empty net effect — absorb.
                    this.lockManager.removePatch(patch.id);
                    absorbedPatchIds.add(patch.id);
                    absorbed++;
                    continue;
                }

                if (hasProtectedFiles) {
                    // Protected files with a real diff: keep patch_content as-is.
                    // Only advance base_generation.
                    patch.base_generation = currentGenSha;
                    try {
                        this.lockManager.updatePatch(patch.id, { base_generation: currentGenSha });
                    } catch {
                        // Not in lockfile yet — in-memory mutation is sufficient.
                    }
                    keptAsUserOwned++;
                    continue;
                }

                // Generator files OR mixed patches with some user-owned files:
                // rebase patch_content to the canonical git-diff against new
                // generation.
                const newContentHash = this.detector.computeContentHash(diff);

                // Consolidate when another patch already captured this exact
                // diff. Two patches with byte-identical post-rebase
                // patch_content encode the SAME customer-side delta — applying
                // both back-to-back at the next regen produces duplicate-apply
                // corruption (the same lines spliced in twice). Consolidate
                // to one canonical patch; per-commit attribution is recoverable
                // from `git log -- <file>`. See preGenerationRebase for the
                // full rationale.
                const priorPatchId = seenContentHashes.get(newContentHash);
                if (priorPatchId !== undefined) {
                    this.lockManager.removePatch(patch.id);
                    absorbedPatchIds.add(patch.id);
                    absorbed++;
                    if (priorPatchId !== patch.id) {
                        this.warnings.push(
                            `Consolidated patch ${patch.id} (commit ${(patch.original_commit || "<missing>").slice(0, 7)}) ` +
                                `into prior patch ${priorPatchId}: byte-identical patch_content after rebase. ` +
                                `Per-commit attribution preserved in git log.`
                        );
                    }
                    continue;
                } else {
                    seenContentHashes.set(newContentHash, patch.id);
                }

                // Mutate in-memory AND lockfile. For new patches (not yet added to
                // the lockfile), updatePatch throws; the in-memory mutation ensures
                // the correct values end up persisted when addPatch runs later.
                patch.base_generation = currentGenSha;
                patch.patch_content = diff;
                patch.content_hash = newContentHash;
                // Snapshot from working tree. Same rationale as the
                // user-owned branch above: only fires for cleanly-applied
                // patches (`status === "applied"` filter at the top of
                // the loop), so disk = correctly-merged customer state at
                // the new generation. Captures rename keys for next regen.
                const snapshot = neutralizedRebase
                    ? neutralizedRebase.snapshot
                    : await this.readSnapshotFromDisk(patch.files);
                const snapshotIsNonEmpty = Object.keys(snapshot).length > 0;
                if (snapshotIsNonEmpty) {
                    patch.theirs_snapshot = snapshot;
                }
                try {
                    this.lockManager.updatePatch(patch.id, {
                        base_generation: currentGenSha,
                        patch_content: diff,
                        content_hash: newContentHash,
                        ...(snapshotIsNonEmpty ? { theirs_snapshot: snapshot } : {})
                    });
                } catch {
                    // Not in lockfile yet — in-memory mutation is sufficient.
                }
                contentRebased++;
            } catch {
                // If rebasing fails for any reason, keep the original patch unchanged
            }
        }

        return { absorbed, repointed, contentRebased, keptAsUserOwned, absorbedPatchIds };
    }

    /**
     * Read THEIRS snapshot (post-customer-edit content) for a list of files
     * from the working tree on disk. Used by `rebasePatches` after a clean
     * apply — disk = correctly-merged customer state at the new generation.
     * Skips binary files (matches `mergeFile`/`populateAccumulatorForPatch`
     * policy — binary content as `utf-8` is lossy and snapshots of it would
     * never be used during apply anyway).
     */
    private async readSnapshotFromDisk(files: string[]): Promise<Record<string, string>> {
        const snapshot: Record<string, string> = {};
        for (const file of files) {
            if (isBinaryFile(file)) continue;
            try {
                const content = await readFileAsync(join(this.outputDir, file), "utf-8");
                snapshot[file] = content;
            } catch {
                // Missing file — omit (the file might have been deleted by the customer).
            }
        }
        return snapshot;
    }

    /**
     * Autoversion-aware replacement for the naive `git diff currentGen -- files`
     * + on-disk snapshot used when rebasing a cleanly-applied patch.
     *
     * The generation tree (BASE) still carries the placeholder on version lines
     * (autoversion runs after `[fern-generated]`), but the working tree holds
     * autoversion's resolved semver. A raw diff/snapshot would fold the
     * placeholder->semver swap into the customer's patch_content/theirs_snapshot,
     * pinning a stale version. This re-derives both against a version-neutralized
     * THEIRS so the pipeline-owned version line never enters the patch.
     *
     * Returns null when no file is under autoversion (fixed `--version`, i.e. no
     * placeholder in the generation tree) — callers keep their existing behavior.
     */
    private async computeAutoversionNeutralizedRebase(
        files: string[],
        currentGenSha: string,
    ): Promise<{ diff: string; snapshot: Record<string, string> } | null> {
        const genByFile = new Map<string, string | null>();
        let anyPlaceholder = false;
        for (const file of files) {
            const gen = await this.git.showFile(currentGenSha, file).catch(() => null);
            genByFile.set(file, gen);
            if (containsPlaceholder(gen)) anyPlaceholder = true;
        }
        if (!anyPlaceholder) return null;

        const snapshot: Record<string, string> = {};
        const fragments: string[] = [];
        for (const file of files) {
            if (isBinaryFile(file)) continue;
            const disk = await readFileAsync(join(this.outputDir, file), "utf-8").catch(() => null);
            if (disk == null) continue;
            const gen = genByFile.get(file) ?? null;
            const neutralized = gen != null ? neutralizeAutoversionTheirs(gen, disk) : disk;
            snapshot[file] = neutralized;
            if (gen == null) {
                fragments.push(synthesizeNewFileDiff(file, neutralized));
            } else if (gen !== neutralized) {
                const fileDiff = await unifiedDiff(gen, neutralized, file);
                if (fileDiff.trim()) fragments.push(fileDiff);
            }
        }
        return { diff: fragments.join(""), snapshot };
    }

    /**
     * Detects autoversion contamination — returns true when a
     * `[fern-autoversion]` commit between `fromSha` and `toSha` touched any
     * of the patch's `files`. Re-classifies each grep match via
     * `isGenerationCommit` so a customer commit that merely quotes the
     * marker in its body doesn't false-positive.
     *
     * Used by preGenerationRebase to skip the customer-content rebuild for
     * patches whose files were last touched by an autoversion step — a
     * naive `git diff currentGen HEAD` there would capture the
     * placeholder→semver swap as customer customization.
     */
    private async hasAutoversionContamination(
        fromSha: string,
        toSha: string,
        files: string[],
    ): Promise<boolean> {
        if (files.length === 0) return false;
        const log = await this.git
            .exec([
                "log",
                `${fromSha}..${toSha}`,
                "--grep=\\[fern-autoversion\\]",
                "--extended-regexp",
                "--format=%H%x09%aN%x09%ae%x09%s",
                "--",
                ...files,
            ])
            .catch(() => "");
        if (!log.trim()) return false;
        for (const line of log.trim().split("\n")) {
            const [sha, authorName, authorEmail, message] = line.split("\t");
            if (sha == null) continue;
            if (
                isGenerationCommit({
                    sha,
                    authorName: authorName ?? "",
                    authorEmail: authorEmail ?? "",
                    message: message ?? "",
                })
            ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Rebuild patch_content from `theirs_snapshot` (captured at detection
     * time, predates pipeline content) rather than a HEAD-relative diff.
     * Returns `null` when snapshot is missing or doesn't cover every file
     * in `patch.files` — caller leaves the patch unchanged.
     *
     * Used as the autoversion-contamination escape hatch: when the
     * HEAD-relative diff would capture pipeline content (autoversion's
     * placeholder swap) as customer customization, the snapshot path
     * preserves the customer's actual intent.
     */
    private async computeSnapshotBasedPatchDiff(
        patch: StoredPatch,
        currentGen: string,
    ): Promise<string | null> {
        if (!patch.theirs_snapshot) return null;
        if (Object.keys(patch.theirs_snapshot).length === 0) return null;
        for (const file of patch.files) {
            if (patch.theirs_snapshot[file] == null) return null;
        }

        const fragments: string[] = [];
        for (const file of patch.files) {
            const theirsContent = patch.theirs_snapshot[file]!;
            const oursContent = await this.git.showFile(currentGen, file).catch(() => null);
            if (oursContent == null) {
                fragments.push(synthesizeNewFileDiff(file, theirsContent));
                continue;
            }
            if (oursContent === theirsContent) continue;
            const fileDiff = await unifiedDiff(oursContent, theirsContent, file);
            if (fileDiff.trim()) {
                fragments.push(fileDiff);
            }
        }
        return fragments.join("");
    }

    /**
     * Read THEIRS snapshot from a git tree-ish (commit, branch, or tree).
     * Used when the diff that produced `patch_content` was relative to that
     * tree (e.g., `git diff currentGen HEAD --` → snapshot from HEAD).
     * Skips binary files.
     */
    private async readSnapshotFromTree(treeRef: string, files: string[]): Promise<Record<string, string>> {
        const snapshot: Record<string, string> = {};
        for (const file of files) {
            if (isBinaryFile(file)) continue;
            const content = await this.git.showFile(treeRef, file).catch(() => null);
            if (content != null) snapshot[file] = content;
        }
        return snapshot;
    }

    /**
     * One-shot auto-migration: backfill `theirs_snapshot` on existing
     * patches that predate the snapshot encoding. Computes the snapshot by
     * applying the patch's `patch_content` to its `base_generation` tree —
     * yielding the patch's INDIVIDUAL contribution.
     *
     * **Skips patches sharing files with other patches.** HEAD's content
     * for a file shared by N patches is cumulative across all of them, so
     * a HEAD-fallback would falsely encode other patches' contributions
     * into one patch's snapshot — breaking cross-patch isolation
     * if the snapshot fallback later fires. Per-patch snapshots only
     * make sense when the patch owns its file. Multi-patch-same-file
     * legacy lockfiles keep using line-anchored reconstruction (the
     * existing path); they're no worse than pre-upgrade.
     *
     * **Does not fall back to HEAD when BASE-reconstruction fails.** Same
     * reason — HEAD content can be cumulative. Patches whose
     * reconstruction fails just don't get a snapshot; legacy paths
     * (PR #73's by-association gate, accumulator fallback) handle them.
     *
     * Skips patches that already have a snapshot.
     */
    private async migratePatchesToSnapshot(patches: StoredPatch[]): Promise<void> {
        const { applyPatchToContent } = await import("./PatchApplyTheirs.js");
        // Identify files that appear in MULTIPLE patches in the lockfile.
        const fileFreq = new Map<string, number>();
        for (const p of patches) {
            for (const f of p.files) {
                fileFreq.set(f, (fileFreq.get(f) ?? 0) + 1);
            }
        }
        let migrated = 0;
        let skipped = 0;
        for (const patch of patches) {
            if (patch.theirs_snapshot != null) continue;
            const sharesAnyFile = patch.files.some((f) => (fileFreq.get(f) ?? 0) > 1);
            if (sharesAnyFile) {
                skipped++;
                continue;
            }
            const snapshot: Record<string, string> = {};
            for (const file of patch.files) {
                if (isBinaryFile(file)) continue;
                try {
                    const base = await this.git.showFile(patch.base_generation, file).catch(() => null);
                    if (base != null) {
                        const content = await applyPatchToContent(base, patch.patch_content, file);
                        if (content != null) snapshot[file] = content;
                    }
                } catch {
                    // Reconstruction failed — leave this file out of the snapshot.
                }
            }
            // Only persist when at least one file's snapshot was computed.
            // Empty snapshots provide no value and would suppress retries.
            if (Object.keys(snapshot).length > 0) {
                patch.theirs_snapshot = snapshot;
                try {
                    this.lockManager.updatePatch(patch.id, { theirs_snapshot: snapshot });
                    migrated++;
                } catch {
                    // Patch not in lockfile yet (shouldn't happen here, but be defensive).
                }
            }
        }
        if (migrated > 0) {
            this.warnings.push(
                `Migrated ${migrated} patch(es) to snapshot encoding. Future regens use 3-way merge ` +
                    `with stored THEIRS, robust to generator structural changes.`
            );
        }
        if (skipped > 0) {
            this.warnings.push(
                `Skipped snapshot migration for ${skipped} patch(es) sharing files with other patches. ` +
                    `These continue to use line-anchored reconstruction (the existing path).`
            );
        }
    }

    /**
     * For conflict patches with mixed results (some files merged, some conflicted),
     * check if the cleanly merged files were absorbed by the generator (empty diff).
     * If so, remove them from patch.files so they don't pollute the pre-generation
     * rebase conflict marker check (`git grep <<<<<<< -- ...patch.files`).
     *
     * Non-absorbed clean files stay in patch.files — removing them would lose
     * the customization on the next generation.
     */
    private async trimAbsorbedFiles(result: ReplayResult, currentGenSha: string): Promise<void> {
        const cleanFiles = result.fileResults!.filter((f) => f.status === "merged").map((f) => f.file);
        if (cleanFiles.length === 0) return;

        const patch = result.patch;
        // If the entire patch is persisted as user-owned, nothing is ever a
        // generator-clean candidate for trimming — short-circuit.
        if (patch.user_owned) return;

        const fernignorePatterns = this.readFernignorePatterns();
        const warnedGens = new Set<string>();

        // Filter to only generator-produced clean files (not user-owned)
        const generatorCleanFiles: string[] = [];
        for (const file of cleanFiles) {
            const userOwned = await this.fileOwnership.resolveCanonical({
                file,
                patch,
                currentGenSha,
                fernignorePatterns,
                warnedGens,
                warnings: this.warnings,
            });
            if (userOwned) {
                continue; // user-owned file — never a candidate for absorption trimming
            }
            generatorCleanFiles.push(file);
        }
        if (generatorCleanFiles.length === 0) return;

        // Check which clean files have an empty diff (absorbed by generator)
        const absorbedFiles = new Set<string>();
        for (const file of generatorCleanFiles) {
            try {
                const diff = await this.git.exec(["diff", currentGenSha, "--", file]).catch(() => null);
                if (!diff || !diff.trim()) {
                    absorbedFiles.add(file);
                }
            } catch {
                // If diff fails, leave the file in the patch
            }
        }
        if (absorbedFiles.size === 0) return;

        // Remove absorbed files from the patch's file list
        const remainingFiles = patch.files.filter((f) => !absorbedFiles.has(f));
        if (remainingFiles.length === patch.files.length) return; // nothing changed

        try {
            this.lockManager.updatePatch(patch.id, { files: remainingFiles });
        } catch {
            // New patches aren't in lockfile yet — update in-memory only
            patch.files = remainingFiles;
        }
    }

    /**
     * Pre-generation rebase: update patches using the customer's current state.
     * Called BEFORE commitGeneration() while HEAD has customer code.
     */
    /** @internal Used by Flow implementations in `src/flows/`. */
    async preGenerationRebase(
        patches: StoredPatch[]
    ): Promise<{ conflictResolved: number; conflictAbsorbed: number; contentRefreshed: number }> {
        const lock = this.lockManager.read();
        const currentGen = lock.current_generation;

        // If the previous run was skip-application, clear the marker and
        // skip all revert detection. Patches are preserved as-is.
        if (this.lockManager.isReplaySkipped()) {
            this.lockManager.clearReplaySkippedAt();
            return { conflictResolved: 0, conflictAbsorbed: 0, contentRefreshed: 0 };
        }

        // Auto-migrate legacy patches lacking `theirs_snapshot` to the new
        // encoding. One-shot per existing patch — once migrated, future
        // regens use the snapshot path. Compute snapshot from BASE +
        // patch_content (same logic the slow apply path uses today and
        // already trusts). Falls back to HEAD content if BASE is GC'd.
        await this.migratePatchesToSnapshot(patches);

        // Detect whether HEAD is a [fern-generated] commit AND it changed
        // *source* files (vs HEAD~1) — the discriminator for the
        // "manually-staged-generation-at-HEAD" bug case where `git diff
        // currentGen HEAD -- file` would produce generator-vs-generator
        // content (not customer intent) and corrupt patch_content/snapshot.
        //
        // Test helpers and infrastructure commits (e.g.,
        // `[fern-generated] store patches` that only touches .fern/) are
        // also generation commits but DON'T change source — they're safe
        // to content-refresh against. The HEAD~1 source-diff check
        // distinguishes these: the bug case mutates a source file vs its
        // parent (customer's last code → generator output), while infra
        // commits don't.
        //
        // Per-patch evaluation happens below — this just precomputes the
        // generation-author signal.
        const headMessage = await this.git.exec(["log", "-1", "--format=%s%n%aN%n%ae", "HEAD"]).catch(() => "");
        const [headMsgLine, headAuthorName, headAuthorEmail] = headMessage.split("\n");
        const headIsGeneration = headMsgLine
            ? isGenerationCommit({
                sha: "HEAD",
                authorName: headAuthorName ?? "",
                authorEmail: headAuthorEmail ?? "",
                message: headMsgLine
            })
            : false;
        const headParentChangedFiles = async (patchFiles: string[]): Promise<Set<string>> => {
            if (patchFiles.length === 0) return new Set();
            const diff = await this.git
                .exec(["diff", "--name-only", "HEAD~1", "HEAD", "--", ...patchFiles])
                .catch(() => "");
            return new Set(diff.trim().split("\n").filter(Boolean));
        };

        let conflictResolved = 0;
        let conflictAbsorbed = 0;
        let contentRefreshed = 0;
        const fernignorePatterns = this.readFernignorePatterns();
        const warnedGens = new Set<string>();
        // Cache reachability checks per-SHA to avoid repeating commitExists/treeExists
        // git subprocess calls for the same base across many patches (N patches ×
        // 2 calls is the main per-cycle cost in long-horizon scenarios like W11).
        const reachabilityCache = new Map<string, boolean>();
        const isReachable = async (sha: string): Promise<boolean> => {
            const cached = reachabilityCache.get(sha);
            if (cached !== undefined) return cached;
            const reachable =
                (await this.git.commitExists(sha)) || (await this.git.treeExists(sha));
            reachabilityCache.set(sha, reachable);
            return reachable;
        };

        // A patch whose every file is user-owned produces an empty diff
        // between currentGen and HEAD (because commitGeneration bundled the file
        // into currentGen — both sides are identical). That empty diff is NOT a
        // revert signal. Only patches with at least one generator-produced file
        // can have their emptiness legitimately interpreted as "user reverted."
        const allPatchFilesUserOwned = async (patch: StoredPatch): Promise<boolean> => {
            if (patch.user_owned) return true;
            if (patch.files.length === 0) return false;
            for (const file of patch.files) {
                const userOwned = await this.fileOwnership.resolveCanonical({
                    file,
                    patch,
                    currentGenSha: currentGen,
                    fernignorePatterns,
                    warnedGens,
                    warnings: this.warnings,
                });
                if (!userOwned) {
                    return false;
                }
            }
            return true;
        };

        for (const patch of patches) {
            // Surface a warning when the patch's declared base_generation
            // is unreachable (shallow clone / aggressive `git gc --prune`). Check
            // this BEFORE any status gating — the warning is a reachability signal
            // independent of whether the patch is currently unresolved/resolving.
            if (
                patch.base_generation &&
                patch.base_generation !== currentGen &&
                !warnedGens.has(patch.base_generation)
            ) {
                if (!(await isReachable(patch.base_generation))) {
                    warnedGens.add(patch.base_generation);
                    this.warnings.push(
                        `Base generation ${patch.base_generation.slice(0, 7)} is unreachable ` +
                            `(shallow clone or pruned history). Treating affected files as user-owned ` +
                            `to avoid silent data loss. ` +
                            `Run \`git fetch --unshallow\` or avoid \`git gc --prune\` to restore full history.`
                    );
                }
            }

            // Skip patches with any status (unresolved/resolving) — they weren't
            // successfully applied last time. Clear status so applyPatches() can
            // try them against the new generation.
            if (patch.status != null) {
                delete patch.status;
                continue;
            }

            // When HEAD is a generation commit AND it mutated this patch's
            // source files vs its parent, the customer manually staged a
            // new generation at HEAD without running replay. Both the
            // content-refresh and stale-base branches below would treat
            // `git diff currentGen → HEAD` as customer intent — but here
            // it's generator-vs-generator structural changes (the customer
            // overwrote their own resolved file with the generator's new
            // output). Refreshing patch_content/snapshot with that diff
            // silently erases the customization. Skip and let the patch
            // survive intact; `mergeFile`'s slow path will reconcile via
            // 3-way merge against the (still-valid) theirs_snapshot.
            //
            // Infrastructure [fern-generated] commits (e.g. test helpers,
            // store-patches commits) don't mutate source files vs parent,
            // so they fall through to the normal refresh path.
            if (headIsGeneration) {
                const parentChangedFiles = await headParentChangedFiles(patch.files);
                const headTouchedSource = patch.files.some((f) => parentChangedFiles.has(f));
                if (headTouchedSource) {
                    continue;
                }
            }

            if (patch.base_generation === currentGen) {
                // Content refresh: check if user modified their customization
                // since the last replay commit. When an autoversion commit
                // contaminated HEAD between replay runs, route via
                // snapshot-based reconstruction to avoid baking the
                // placeholder→semver swap into customer patch_content.
                try {
                    const contaminated = await this.hasAutoversionContamination(
                        currentGen,
                        "HEAD",
                        patch.files,
                    );

                    if (contaminated) {
                        const snapshotDiff = await this.computeSnapshotBasedPatchDiff(
                            patch,
                            currentGen,
                        );
                        if (snapshotDiff === null) continue;
                        if (!snapshotDiff.trim()) {
                            if (await allPatchFilesUserOwned(patch)) continue;
                            this.lockManager.removePatch(patch.id);
                            contentRefreshed++;
                            continue;
                        }
                        const snapHasStaleMarkers = snapshotDiff.split("\n").some(
                            (l) => l.startsWith("+<<<<<<< Generated") || l.startsWith("+>>>>>>> Your customization")
                        );
                        if (snapHasStaleMarkers) continue;
                        const isProtectedSnap = patch.files.some(
                            (file) => file === ".fernignore" ||
                                fernignorePatterns.some((p) => file === p || minimatch(file, p))
                        );
                        if (isProtectedSnap) continue;
                        const snapHash = this.detector.computeContentHash(snapshotDiff);
                        if (snapHash !== patch.content_hash) {
                            this.lockManager.updatePatch(patch.id, {
                                patch_content: snapshotDiff,
                                content_hash: snapHash,
                            });
                            contentRefreshed++;
                        }
                        continue;
                    }

                    const ppResult = await computePerPatchDiffWithFallback(
                        patch,
                        (f) => this.git.showFile(currentGen, f),
                        (f) => this.git.showFile("HEAD", f),
                        (files) => this.git
                            .exec(["diff", currentGen, "HEAD", "--", ...files])
                            .catch(() => null)
                    );

                    if (ppResult === null) continue;
                    if (ppResult.hadFallback) {
                        this.warnings.push(
                            `Patch ${patch.id}: couldn't fully isolate ${ppResult.fallbackFiles.join(", ")} — ` +
                                `customization preserved, attribution may have merged with another patch.`
                        );
                    }
                    const diff = ppResult.diff;

                    if (!diff.trim()) {
                        // Empty diff usually means customer reverted the
                        // customization. But if every file is user-owned, empty
                        // diff is expected (the generator bundled the files
                        // into currentGen) — preserve the patch.
                        if (await allPatchFilesUserOwned(patch)) continue;
                        // User reverted all customizations in this patch
                        this.lockManager.removePatch(patch.id);
                        contentRefreshed++;
                        continue;
                    }

                    // Skip content refresh if the diff contains stale conflict
                    // markers from a previous crashed run. Only check added lines
                    // (prefixed with "+") to avoid false positives from files that
                    // legitimately contain marker text (e.g., documentation).
                    const diffLines = diff.split("\n");
                    const hasStaleMarkers = diffLines.some(
                        (l) => l.startsWith("+<<<<<<< Generated") || l.startsWith("+>>>>>>> Your customization")
                    );
                    if (hasStaleMarkers) {
                        continue;
                    }

                    // Skip content conversion for .fernignore-protected files —
                    // matches rebasePatches' behavior so both flows end with
                    // identical patch_content for the same scenario.
                    // User-created (non-protected) files go through the regular
                    // content-refresh path so they can be deduplicated when
                    // multiple incremental patches resolve to the same cumulative
                    // state.
                    const isProtected = patch.files.some(
                        (file) => file === ".fernignore" ||
                            fernignorePatterns.some((p) => file === p || minimatch(file, p))
                    );
                    if (isProtected) {
                        continue;
                    }

                    const newContentHash = this.detector.computeContentHash(diff);
                    if (newContentHash !== patch.content_hash) {
                        // User changed their customization — update patch content
                        // and recalculate the files list (some files may no longer differ)
                        const filesOutput = await this.git
                            .exec(["diff", "--name-only", currentGen, "HEAD", "--", ...patch.files])
                            .catch(() => null);

                        const newFiles = filesOutput
                            ? filesOutput
                                  .trim()
                                  .split("\n")
                                  .filter(Boolean)
                                  .filter((f) => !f.startsWith(".fern/"))
                            : patch.files;

                        if (newFiles.length === 0) {
                            this.lockManager.removePatch(patch.id);
                        } else {
                            // Snapshot THEIRS from HEAD (the diff is
                            // currentGen → HEAD). The `headIsGeneration`
                            // early-continue above already protects against
                            // the manual-gen-commit-at-HEAD case (where HEAD
                            // is generator content with no customer intent).
                            // For genuine customer edits, HEAD is the
                            // authoritative customer state — refresh
                            // snapshot to match so subsequent regens see
                            // the customer's latest intent.
                            const snapshot = await this.readSnapshotFromTree("HEAD", newFiles);
                            this.lockManager.updatePatch(patch.id, {
                                patch_content: diff,
                                content_hash: newContentHash,
                                files: newFiles,
                                ...(Object.keys(snapshot).length > 0 ? { theirs_snapshot: snapshot } : {})
                            });
                        }
                        contentRefreshed++;
                    }
                } catch {
                    // If anything fails, leave the patch unchanged
                }
                continue;
            }

            try {
                // Check for unresolved conflict markers in HEAD
                const markerFiles = await this.git
                    .exec(["grep", "-l", "<<<<<<< Generated", "HEAD", "--", ...patch.files])
                    .catch(() => "");

                if (markerFiles.trim()) continue;

                // Snapshot-based reconstruction when autoversion contaminated.
                const contaminated = await this.hasAutoversionContamination(
                    currentGen,
                    "HEAD",
                    patch.files,
                );

                if (contaminated) {
                    const snapshotDiff = await this.computeSnapshotBasedPatchDiff(
                        patch,
                        currentGen,
                    );
                    if (snapshotDiff === null) continue;
                    if (!snapshotDiff.trim()) {
                        if (await allPatchFilesUserOwned(patch)) {
                            try {
                                this.lockManager.updatePatch(patch.id, { base_generation: currentGen });
                            } catch {
                                // New patch — not in lockfile yet
                            }
                            continue;
                        }
                        this.lockManager.removePatch(patch.id);
                        conflictAbsorbed++;
                        continue;
                    }
                    const snapHasStaleMarkers = snapshotDiff.split("\n").some(
                        (l) => l.startsWith("+<<<<<<< Generated") || l.startsWith("+>>>>>>> Your customization")
                    );
                    if (snapHasStaleMarkers) continue;
                    const snapHash = this.detector.computeContentHash(snapshotDiff);
                    this.lockManager.updatePatch(patch.id, {
                        base_generation: currentGen,
                        patch_content: snapshotDiff,
                        content_hash: snapHash,
                    });
                    conflictResolved++;
                    continue;
                }

                // Per-patch-region diff with cumulative fallback for the
                // unlocatable subset (see content-refresh branch above for
                // the rationale around partially-anchorable hunks).
                const ppResult = await computePerPatchDiffWithFallback(
                    patch,
                    (f) => this.git.showFile(currentGen, f),
                    (f) => this.git.showFile("HEAD", f),
                    (files) => this.git
                        .exec(["diff", currentGen, "HEAD", "--", ...files])
                        .catch(() => null)
                );

                // ppResult === null only when cumulative fallback explicitly
                // failed — skip the patch rather than corrupt the lockfile.
                if (ppResult === null) continue;
                if (ppResult.hadFallback) {
                    this.warnings.push(
                        `Patch ${patch.id}: couldn't fully isolate ${ppResult.fallbackFiles.join(", ")} — ` +
                            `customization preserved, attribution may have merged with another patch.`
                    );
                }
                const diff = ppResult.diff;

                if (!diff.trim()) {
                    // Empty diff: for user-owned patches this is expected
                    // (currentGen has bundled copies), not a revert signal.
                    if (await allPatchFilesUserOwned(patch)) {
                        // Repoint stale base onto current so the next cycle
                        // routes via the fast content-refresh branch.
                        try {
                            this.lockManager.updatePatch(patch.id, { base_generation: currentGen });
                        } catch {
                            // New patches aren't in lockfile yet
                        }
                        continue;
                    }
                    // Empty diff — customer reverted their customization
                    this.lockManager.removePatch(patch.id);
                    conflictAbsorbed++;
                    continue;
                }

                // Update the patch with the resolved content. The
                // `headIsGeneration` early-continue above protects the
                // bug case (HEAD is a generator commit with no customer
                // intent). For genuine customer resolutions, refresh
                // snapshot from HEAD too.
                const newContentHash = this.detector.computeContentHash(diff);
                const snapshot = await this.readSnapshotFromTree("HEAD", patch.files);
                this.lockManager.updatePatch(patch.id, {
                    base_generation: currentGen,
                    patch_content: diff,
                    content_hash: newContentHash,
                    ...(Object.keys(snapshot).length > 0 ? { theirs_snapshot: snapshot } : {})
                });
                conflictResolved++;
            } catch {
                // If anything fails, leave the patch unchanged
            }
        }

        return { conflictResolved, conflictAbsorbed, contentRefreshed };
    }

    /**
     * After applyPatches(), surface a warning for each (patch × file) where
     * diff3 silently dropped lines that were unchanged context in the
     * customer's THEIRS. The deletion stays on disk (correct diff3 outcome),
     * but the customer must learn so they can re-add the lines if they
     * relied on them. This is the "surface or preserve, never silently drop"
     * contract for legitimate-deletion cases.
     */
    /** @internal Used by Flow implementations in `src/flows/`. */
    recordDroppedContextLineWarnings(results: ReplayResult[]): void {
        const PREVIEW_COUNT = 5;
        for (const result of results) {
            if (!result.fileResults) continue;
            for (const fr of result.fileResults) {
                const dropped = fr.droppedContextLines;
                if (!dropped || dropped.length === 0) continue;
                const preview = dropped
                    .slice(0, PREVIEW_COUNT)
                    .map((line) => `    ${line}`)
                    .join("\n");
                const more = dropped.length > PREVIEW_COUNT
                    ? `\n    ... and ${dropped.length - PREVIEW_COUNT} more`
                    : "";
                this.warnings.push(
                    `${fr.file}: ${dropped.length} line(s) that appeared as unchanged ` +
                        `context in your customization were deleted by the new generator output. ` +
                        `The merge followed the deletion. First lines deleted:\n${preview}${more}\n` +
                        `If you want to keep these lines, restore them in a follow-up commit and ` +
                        `re-run replay; otherwise this warning can be safely ignored.`
                );
            }
        }
    }

    /**
     * After applyPatches(), surface a warning for each (patch × file) where a
     * customization's BASE could not be read because the patch's
     * base-generation tree is unreachable in this clone (shallow-clone window
     * too small / pruned history). In that state the merge pipeline falls
     * through to `new-file-both` and the customer's edit can be dropped with NO
     * conflict and NO error — the INC-866 silent-loss class. This converts that
     * silence into an explicit, actionable warning naming the file and the
     * likely cause (insufficient clone window).
     *
     * Mirrors `recordDroppedContextLineWarnings`'s "surface, never silently
     * drop" contract. De-duplicated per file path so a single insufficient
     * window doesn't spam one warning per touched file repeatedly.
     */
    /** @internal Used by Flow implementations in `src/flows/`. */
    recordUnreachableBaseWarnings(results: ReplayResult[]): void {
        const warned = new Set<string>();
        for (const result of results) {
            if (!result.fileResults) continue;
            for (const fr of result.fileResults) {
                if (!fr.baseTreeUnreachable) continue;
                if (warned.has(fr.file)) continue;
                warned.add(fr.file);
                const base = fr.conflictMetadata?.baseGeneration;
                const baseRef = base ? ` (base generation ${base.slice(0, 7)})` : "";
                this.warnings.push(
                    `${fr.file}: could not read the prior generation's version of this ` +
                        `file${baseRef} because that generation's tree is outside the clone ` +
                        `window (insufficient shallow-clone depth or pruned history). The ` +
                        `customization was treated as a new file, which can drop your edits ` +
                        `silently. Re-run with the prior generation in the clone window — e.g. ` +
                        `\`git fetch --shallow-since=<date just before the prior [fern-generated] ` +
                        `commit>\` or \`git fetch --unshallow\` — then re-run replay.`
                );
            }
        }
        // Structured channel: ONE summarizing degraded reason for the run
        // (per-file detail already lives in the warnings above).
        if (warned.size > 0) {
            this.degradedReasons.push({
                code: "patch-base-unreachable",
                message:
                    `${warned.size} customization file(s) could not be checked against the ` +
                    `generation they were made on because that generation's tree was not ` +
                    `reachable during this run. Customizations may not have been carried ` +
                    `forward — review the result before merging, or widen the clone window ` +
                    `and re-run replay.`,
            });
        }
    }

    /**
     * After applyPatches(), strip conflict markers from conflicting files
     * so only clean content is committed. Keeps the Generated (OURS) side.
     */
    /** @internal Used by Flow implementations in `src/flows/`. */
    revertConflictingFiles(results: ReplayResult[]): void {
        for (const result of results) {
            if (result.status !== "conflict" || !result.fileResults) continue;

            for (const fileResult of result.fileResults) {
                if (fileResult.status !== "conflict") continue;

                const filePath = join(this.outputDir, fileResult.file);
                try {
                    const content = readFileSync(filePath, "utf-8");
                    const stripped = stripConflictMarkers(content);
                    writeFileSync(filePath, stripped);
                } catch {
                    // If file doesn't exist or can't be read, skip
                }
            }
        }
    }

    /**
     * Clean up stale conflict markers left by a previous crashed run.
     * Called after commitGeneration() when HEAD is the [fern-generated] commit.
     * Restores files to their clean generated state from HEAD.
     * Skips .fernignore-protected files to prevent overwriting user content.
     */
    /** @internal Used by Flow implementations in `src/flows/`. */
    async cleanupStaleConflictMarkers(): Promise<void> {
        const markerFiles = await this.git
            .exec(["grep", "-l", "<<<<<<< Generated", "--", "."])
            .catch(() => "");

        const files = markerFiles.trim().split("\n").filter(Boolean);
        if (files.length === 0) return;

        const fernignorePatterns = this.readFernignorePatterns();

        for (const file of files) {
            if (fernignorePatterns.some((pattern) => minimatch(file, pattern))) continue;

            try {
                await this.git.exec(["checkout", "HEAD", "--", file]);
            } catch {
                // File may not exist in the generation tree (e.g., user-created
                // file). Skip — revertConflictingFiles will handle it later.
            }
        }
    }

    /** @internal Used by Flow implementations in `src/flows/`. */
    readFernignorePatterns(): string[] {
        return readFernignorePatterns(this.outputDir);
    }

    /** @internal Used by Flow implementations in `src/flows/`. */
    buildReport(
        flow: "first-generation" | "no-patches" | "normal-regeneration" | "skip-application",
        patches: StoredPatch[],
        results: ReplayResult[],
        options?: ReplayOptions,
        warnings?: string[],
        rebaseCounts?: {
            absorbed: number;
            repointed: number;
            contentRebased: number;
            keptAsUserOwned: number;
            absorbedPatchIds: Set<string>;
        },
        preRebaseCounts?: { conflictResolved: number; conflictAbsorbed: number; contentRefreshed: number },
        patchesReverted?: number,
        degradedReasons?: DegradedReason[]
    ): ReplayReport {
        const degraded = dedupeDegradedReasons(degradedReasons ?? []);
        const conflictResults = results.filter((r) => r.status === "conflict");
        const conflictDetails = conflictResults
            .map((r) => {
                const conflictFiles = r.fileResults?.filter((f) => f.status === "conflict") ?? [];
                const cleanFiles = r.fileResults?.filter((f) => f.status === "merged").map((f) => f.file) ?? [];
                return {
                    patchId: r.patch.id,
                    patchMessage: r.patch.original_message,
                    reason: r.conflictReason,
                    files: conflictFiles,
                    cleanFiles: cleanFiles.length > 0 ? cleanFiles : undefined
                };
            })
            .filter((d) => d.files.length > 0);
        const partialCount = conflictDetails.filter((d) => d.cleanFiles && d.cleanFiles.length > 0).length;

        // Widen unresolvedPatches to include conflict-by-association entries:
        // patches whose result.status is "applied" but whose patch.status was
        // set to "unresolved" by the rebase guard (clean follow-up after
        // same-file conflict). They have no file-level merge conflicts of
        // their own but must surface so the resolve workflow handles them.
        const unresolvedResults = results.filter(
            (r) => r.status === "conflict" || r.patch.status === "unresolved"
        );

        return {
            flow,
            patchesDetected: patches.length,
            patchesApplied: results.filter((r) => r.status === "applied").length,
            patchesWithConflicts: conflictResults.length,
            patchesSkipped: results.filter((r) => r.status === "skipped").length,
            patchesPartiallyApplied: partialCount > 0 ? partialCount : undefined,
            patchesAbsorbed: rebaseCounts && rebaseCounts.absorbed > 0 ? rebaseCounts.absorbed : undefined,
            patchesRepointed: rebaseCounts && rebaseCounts.repointed > 0 ? rebaseCounts.repointed : undefined,
            patchesContentRebased:
                rebaseCounts && rebaseCounts.contentRebased > 0 ? rebaseCounts.contentRebased : undefined,
            patchesKeptAsUserOwned:
                rebaseCounts && rebaseCounts.keptAsUserOwned > 0 ? rebaseCounts.keptAsUserOwned : undefined,
            patchesReverted: patchesReverted && patchesReverted > 0 ? patchesReverted : undefined,
            patchesConflictResolved:
                preRebaseCounts && preRebaseCounts.conflictResolved + preRebaseCounts.conflictAbsorbed > 0
                    ? preRebaseCounts.conflictResolved + preRebaseCounts.conflictAbsorbed
                    : undefined,
            patchesRefreshed:
                preRebaseCounts && preRebaseCounts.contentRefreshed > 0 ? preRebaseCounts.contentRefreshed : undefined,
            conflicts: conflictResults.flatMap((r) => r.fileResults?.filter((f) => f.status === "conflict") ?? []),
            conflictDetails: conflictDetails.length > 0 ? conflictDetails : undefined,
            unresolvedPatches:
                unresolvedResults.length > 0
                    ? unresolvedResults.map((r) => ({
                          patchId: r.patch.id,
                          patchMessage: r.patch.original_message,
                          files: r.patch.files,
                          conflictDetails: r.fileResults?.filter((f) => f.status === "conflict") ?? []
                      }))
                    : undefined,
            wouldApply: options?.dryRun ? patches : undefined,
            warnings: warnings && warnings.length > 0 ? warnings : undefined,
            degraded: degraded.length > 0 ? true : undefined,
            degradedReasons: degraded.length > 0 ? degraded : undefined
        };
    }
}

/**
 * Merge degraded-reason lists, dropping exact duplicates (same code AND
 * message). A run can legitimately hit multiple set-points (e.g. anchor
 * unreachable at detect time AND patch base unreachable at apply time);
 * distinct reasons are all kept.
 *
 * @internal Used by Flow implementations in `src/flows/`.
 */
export function dedupeDegradedReasons(reasons: DegradedReason[]): DegradedReason[] {
    const seen = new Set<string>();
    const out: DegradedReason[] = [];
    for (const reason of reasons) {
        const key = `${reason.code} ${reason.message}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(reason);
    }
    return out;
}

/**
 * Bucket the patches given to commitReplay so the commit message body is
 * an honest reflection of outcomes. A patch is:
 *   - `unresolved`: file-level merge conflict OR PR #73 conflict-by-association
 *     (`patch.status === "unresolved"` set by `rebasePatches`)
 *   - `absorbed`: in `absorbedPatchIds` (rebase removed it because the
 *     generator now emits the customization natively)
 *   - `applied`: everything else with status === "applied"
 * Skipped/binary patches don't surface in the commit message at all.
 *
 * `patchesPassedToCommit` is what the caller hands to `commitReplay` (e.g.,
 * `allPatches` for normal regen, `newPatches` for the no-patches flow).
 * The function preserves its order so the commit body matches lockfile order.
 */
/** @internal Used by Flow implementations in `src/flows/`. */
export function computeCommitBuckets(
    results: ReplayResult[],
    absorbedPatchIds: Set<string>,
    patchesPassedToCommit: StoredPatch[]
): { applied: StoredPatch[]; unresolved: StoredPatch[]; absorbed: StoredPatch[] } {
    const resultByPatchId = new Map<string, ReplayResult>();
    for (const r of results) resultByPatchId.set(r.patch.id, r);

    const applied: StoredPatch[] = [];
    const unresolved: StoredPatch[] = [];
    const absorbed: StoredPatch[] = [];

    for (const patch of patchesPassedToCommit) {
        if (absorbedPatchIds.has(patch.id)) {
            absorbed.push(patch);
            continue;
        }
        const r = resultByPatchId.get(patch.id);
        if (!r) {
            // No result (e.g., patch wasn't applied this cycle but is in
            // the lockfile). Treat as applied for legacy parity — the
            // alternative would silently disappear from the commit body.
            applied.push(patch);
            continue;
        }
        if (r.status === "conflict" || patch.status === "unresolved") {
            unresolved.push(patch);
            continue;
        }
        if (r.status === "applied") {
            applied.push(patch);
            continue;
        }
        // status === "skipped" — omit. Skipped patches typically come from
        // exclude patterns; surfacing them in the commit body is noise.
    }

    return { applied, unresolved, absorbed };
}

/**
 * Result of cleansing a single patch against the current `.fernignore`
 * patterns. Discriminated three ways:
 *   - `unchanged`: patch had no fernignore-matched references; leave alone.
 *   - `remove`: patch's surviving files / patch_content are both empty;
 *     remove the patch from the lockfile.
 *   - otherwise: patch survives with `files` / `patch_content` /
 *     `theirs_snapshot` / `content_hash` updated.
 *
 * @internal Exported for unit tests; called from
 * `ReplayService.cleanseLegacyFernignoreEntries`.
 */
export interface LegacyFernignoreCleanseResult {
    readonly unchanged: boolean;
    readonly remove: boolean;
    readonly files: string[];
    readonly patch_content: string;
    readonly content_hash: string;
    readonly theirs_snapshot?: Record<string, string>;
    readonly strippedPaths: string[];
}

/**
 * Pure function — operates on `StoredPatch` fields and the current
 * `.fernignore` patterns. Returns what the cleanse step in
 * `ReplayService.prepareReplay` should do for this patch.
 *
 * Safety properties:
 *   - Returns `unchanged: true` if no fernignore-matched paths appear
 *     anywhere in `files`, `theirs_snapshot`, or `patch_content`.
 *   - Leaves `patch_content` byte-identical when no `diff --git` headers
 *     can be parsed (fail-safe for exotic legacy formats — the applicator
 *     iterates `files`, not `patch_content`, so the fernignored file is
 *     still untouched).
 *   - `content_hash` is only recomputed when `patch_content` actually
 *     changes. Otherwise the original hash is preserved.
 */
export function computeLegacyFernignoreCleanse(
    patch: StoredPatch,
    fernignorePatterns: readonly string[],
    computeContentHash: (content: string) => string,
): LegacyFernignoreCleanseResult {
    const matchesFernignore = (filePath: string): boolean =>
        fernignorePatterns.some((p) => {
            if (filePath === p) return true;
            if (minimatch(filePath, p)) return true;
            // Gitignore-style: a bare directory name also matches all
            // paths underneath it. See ReplayDetector for rationale.
            if (!p.includes("*") && !p.includes("?") && filePath.startsWith(p + "/")) return true;
            return false;
        });

    const strippedFromFiles = patch.files.filter(matchesFernignore);
    const survivingFiles = patch.files.filter((f) => !matchesFernignore(f));

    let survivingSnapshot: Record<string, string> | undefined = undefined;
    let snapshotChanged = false;
    if (patch.theirs_snapshot) {
        survivingSnapshot = {};
        for (const [key, value] of Object.entries(patch.theirs_snapshot)) {
            if (matchesFernignore(key)) {
                snapshotChanged = true;
            } else {
                survivingSnapshot[key] = value;
            }
        }
    }

    const { stripped: patchContentStripped, content: newPatchContent, strippedSectionPaths } =
        stripDiffSectionsByPredicate(patch.patch_content, matchesFernignore);

    const unchanged = strippedFromFiles.length === 0 && !snapshotChanged && !patchContentStripped;
    if (unchanged) {
        return {
            unchanged: true,
            remove: false,
            files: patch.files,
            patch_content: patch.patch_content,
            content_hash: patch.content_hash,
            theirs_snapshot: patch.theirs_snapshot,
            strippedPaths: [],
        };
    }

    const strippedPaths = Array.from(new Set([...strippedFromFiles, ...strippedSectionPaths]));

    if (survivingFiles.length === 0 || newPatchContent.trim() === "") {
        return {
            unchanged: false,
            remove: true,
            files: [],
            patch_content: "",
            content_hash: "",
            strippedPaths,
        };
    }

    const newContentHash = patchContentStripped
        ? computeContentHash(newPatchContent)
        : patch.content_hash;

    return {
        unchanged: false,
        remove: false,
        files: survivingFiles,
        patch_content: newPatchContent,
        content_hash: newContentHash,
        ...(survivingSnapshot !== undefined ? { theirs_snapshot: survivingSnapshot } : {}),
        strippedPaths,
    };
}

/**
 * Infrastructure file predicate — matches files managed by the generation
 * pipeline that must never appear in patch content. Mirrors the
 * `INFRASTRUCTURE_FILES` constant in `ReplayDetector.ts`.
 */
function isInfrastructureFile(filePath: string): boolean {
    return filePath === ".fernignore" || filePath.startsWith(".fern/");
}

interface InfrastructureFileCleanseResult {
    unchanged: boolean;
    remove: boolean;
    patch_content: string;
    content_hash: string;
    strippedPaths: string[];
}

/**
 * Strips infrastructure file diff sections (`.fernignore`, `.fern/*`) from a
 * patch's `patch_content`. These were bundled by older detection logic that
 * used `git format-patch` without scoping to surviving files when only
 * infrastructure files were stripped.
 *
 * Only operates on `patch_content` — infrastructure files never appear in
 * `patch.files` or `theirs_snapshot` (those were correctly filtered at
 * detection time). The bug was solely that `format-patch` captured the full
 * commit diff.
 */
export function computeInfrastructureFileCleanse(
    patch: StoredPatch,
    computeContentHash: (content: string) => string,
): InfrastructureFileCleanseResult {
    const { stripped, content: newPatchContent, strippedSectionPaths } =
        stripDiffSectionsByPredicate(patch.patch_content, isInfrastructureFile);

    if (!stripped) {
        return {
            unchanged: true,
            remove: false,
            patch_content: patch.patch_content,
            content_hash: patch.content_hash,
            strippedPaths: [],
        };
    }

    // All diff sections were infrastructure files — the patch has no
    // surviving SDK content.
    if (newPatchContent.trim() === "" || patch.files.length === 0) {
        return {
            unchanged: false,
            remove: true,
            patch_content: "",
            content_hash: "",
            strippedPaths: strippedSectionPaths,
        };
    }

    return {
        unchanged: false,
        remove: false,
        patch_content: newPatchContent,
        content_hash: computeContentHash(newPatchContent),
        strippedPaths: strippedSectionPaths,
    };
}

/**
 * Syntactic stripper for `diff --git` sections. Drops sections whose
 * target (`b/<path>`) matches the predicate. Fail-safe behavior:
 *
 *   - If `patch_content` has NO `diff --git` headers, returns it
 *     unchanged. The cleanse step relies on this to avoid corrupting
 *     legacy or exotic patch formats.
 *   - If a `diff --git` line's path cannot be parsed (e.g., git-quoted
 *     form with special characters), the section is KEPT — false
 *     positives are zero by design.
 */
function stripDiffSectionsByPredicate(
    patchContent: string,
    shouldStrip: (filePath: string) => boolean,
): { stripped: boolean; content: string; strippedSectionPaths: string[] } {
    const lines = patchContent.split("\n");
    const sectionStarts: { idx: number; path: string | null }[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]!;
        if (line.startsWith("diff --git ")) {
            const match = line.match(/^diff --git a\/(.+?) b\/(.+?)$/);
            const path = match ? match[2]! : null;
            sectionStarts.push({ idx: i, path });
        }
    }

    if (sectionStarts.length === 0) {
        return { stripped: false, content: patchContent, strippedSectionPaths: [] };
    }

    const preamble = lines.slice(0, sectionStarts[0]!.idx);
    const newLines: string[] = [...preamble];
    const strippedSectionPaths: string[] = [];
    let stripped = false;

    for (let i = 0; i < sectionStarts.length; i++) {
        const section = sectionStarts[i]!;
        const next = sectionStarts[i + 1];
        const endIdx = next ? next.idx : lines.length;
        if (section.path !== null && shouldStrip(section.path)) {
            stripped = true;
            strippedSectionPaths.push(section.path);
            continue;
        }
        newLines.push(...lines.slice(section.idx, endIdx));
    }

    return { stripped, content: newLines.join("\n"), strippedSectionPaths };
}
